import { HttpClient, HttpEventType } from '@angular/common/http'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { NgIf, NgFor } from '@angular/common';

@Component({
    selector: 'file-uploader',
    styleUrls: ['./file-uploader.component.scss'],
    templateUrl: './file-uploader.component.html',
    standalone: true,
    imports: [NgIf, NgFor],
})
export class ComponentFileUploader {
	@Input() folder = ''
	@Input() cropX: number | null = null
	@Input() cropY: number | null = null
	@Input() preview: boolean = false
	@Output() progress = new EventEmitter<{ file: string; type: string; progress?: number }>()
	@Output() files = new EventEmitter<{ file: string; url: string }>()
	imageUrls: string[] = []

	constructor(private http: HttpClient) {}

	preventDefault(event: Event) {
		event.preventDefault()
		event.stopPropagation()
	}

	handleDrop(event: DragEvent) {
		this.preventDefault(event)
		if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
			for (let i = 0; i < event.dataTransfer.files.length; i++) {
				const file = event.dataTransfer.files[i]
				this.processFile(file, i)
			}
		}
	}

	onFileChange(event: Event) {
		const target = event.target as HTMLInputElement
		if (target.files && target.files.length > 0) {
			for (let i = 0; i < target.files.length; i++) {
				let file = target.files[i]
				this.processFile(file, i)
			}
		}
	}

	private isImage(file: File): boolean {
		return ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'].includes(file.type)
	}

	private uploadToS3(signedUrl: string, file: File): Observable<number> {
		return this.http
			.put(signedUrl, file, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(
				map((event) => {
					switch (event.type) {
						case HttpEventType.UploadProgress:
							if (event.total) {
								// Calculate and return the progress as a percentage.
								return Math.round((100 * event.loaded) / event.total)
							}
							break
						default:
							return 0
					}
				}),
			)
	}

	private processFile(file, i) {
		const reader = new FileReader()
		reader.onload = () => {
			const img = new Image()
			img.src = reader.result as string
			img.onload = async () => {
				const canvas = document.createElement('canvas')
				const ctx = canvas.getContext('2d')

				// Desired dimensions
				const targetWidth = this.cropX
				const targetHeight = this.cropY
				if (targetWidth && targetHeight) {
					// Calculate the center of the image
					const centerX = img.width / 2
					const centerY = img.height / 2

					// Calculate the coordinates of the top-left corner of the area to be cropped
					const cropX = centerX - targetWidth / 2
					const cropY = centerY - targetHeight / 2

					// Ensure the cropping area fits within the image
					const cropWidth = Math.min(targetWidth, img.width)
					const cropHeight = Math.min(targetHeight, img.height)

					canvas.width = targetWidth
					canvas.height = targetHeight

					ctx.drawImage(
						img,
						cropX,
						cropY,
						cropWidth,
						cropHeight, // Source coordinates and dimensions
						0,
						0,
						targetWidth,
						targetHeight, // Destination coordinates and dimensions
					)
					const response = await fetch(canvas.toDataURL(file.type))
					const blob = await response.blob()
					file = new File([blob], file.name, { type: file.type })
				}
				if (file && this.isImage(file)) {
					this.http
						.get<{ presignedUrl: string; resourceUrl: string }>(
							environment.apiUrl +
								'/api/file/presigned-put-url?key=' +
								this.folder +
								file.name +
								'&type=' +
								file.type,
						)
						.subscribe((response) => {
							const signedUrl = response.presignedUrl
							this.imageUrls[i] = URL.createObjectURL(file)
							this.uploadToS3(signedUrl, file).subscribe(
								(progress) => {
									if (progress !== undefined) {
										this.progress.emit({ file: file.name, type: 'uploading', progress })
									}
								},
								(err) => {
									this.progress.emit({ file: file.name, type: 'errored' })
								},
								() => {
									this.progress.emit({ file: file.name, type: 'completed' })
									this.files.emit({ file: file.name, url: response.resourceUrl })
								},
							)
						})
				}
			}
		}
		reader.readAsDataURL(file)
	}
}
